/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import type { ExtensionParams } from '@atlaskit/editor-common/extensions';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import type { Node, Schema } from '@atlaskit/editor-prosemirror/model';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import { Text } from '@atlaskit/primitives/compiled';
import { ReactRenderer } from '@atlaskit/renderer';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';
import { ADFEncoder } from '@atlaskit/renderer/utils';
import SectionMessage from '@atlaskit/section-message/message';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import type { AIPanelParameters } from '../types';
import {
	getAupViolationMessage,
	getErrorMessage,
	getInternalServerErrorMessage,
	getRateLimitErrorMessage,
} from '../utils';

import { useAIPanelAnalytics } from './utils';

const jsonTransformer = new JSONTransformer();

const prismBackground = css({
	borderRadius: `calc(${token('radius.small', '3px')} + ${token('border.width')})`,
	paddingTop: token('border.width'),
	paddingRight: token('border.width'),
	paddingBottom: token('border.width'),
	paddingLeft: token('border.width'),
});

const prismBackgroundLight = css({
	background: `conic-gradient(
		from var(--panel-gradient-angle, 270deg),
		#0065FF 0%,
		#0469FF 20%,
		#BF63F3 50%,
		#FFA900 56%,
		#0065FF 100%
	)`,
});
const prismBackgroundDark = css({
	background: `conic-gradient(
		from var(--panel-gradient-angle, 270deg),
		#0065FF80 0%,
		#0469FF80 20%,
		#BF63F380 50%,
		#FFA90080 56%,
		#0065FF80 100%
	)`,
});

const aiPanel = css({
	backgroundColor: token('elevation.surface.overlay'),
	borderRadius: token('radius.small'),
});

const aiPanelContainer = css({
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.200'),
});

type Props = {
	node: ExtensionParams<AIPanelParameters>;
	providerFactory?: ProviderFactory;
};

type NodeContent = {
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	attrs?: any;
	content?: NodeContent[];
	text?: string;
	type: string;
};

const createNodesFromContent = (content: NodeContent[], schema: Schema): readonly Node[] => {
	const nodes = [];
	for (const node of content) {
		if (node?.type === 'text') {
			const textNode = schema.text(node.text ?? '');
			nodes.push(textNode);
		} else if (schema.nodes[node.type] && node.content) {
			const contentNodes = createNodesFromContent(node.content, schema);
			const newNode = schema.nodes[node.type].create(node.attrs, contentNodes);
			nodes.push(newNode);
		}
	}
	return nodes;
};

export const AIPanelRenderer = ({ node, providerFactory }: Props): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { colorMode } = useThemeObserver();
	const [errorMessage, setErrorMessage] = useState<ReactNode | string | undefined>(undefined);
	const [theme, setTheme] = useState<'light' | 'dark' | undefined>(undefined);

	useEffect(() => {
		const status = node.parameters?.macroMetadata?.status;
		if (status === 'failed' || status === 'aup-violation') {
			switch (node.parameters?.content) {
				case 'ACCEPTABLE_USE_VIOLATIONS':
					const aupViolationMessage = getAupViolationMessage(formatMessage);
					setErrorMessage(aupViolationMessage);
					break;
				case 'NO_AGENT':
				case 'FEATURE_DISABLED_ON_SITE':
				case 'FEATURE_DISABLED':
				case 'INTERNAL_SERVER_ERROR':
				case 'PLUGIN_ERRORED':
					const internalServerError = getInternalServerErrorMessage(formatMessage);
					setErrorMessage(internalServerError);
					break;
				case 'RATE_LIMIT':
				case 'OPENAI_RATE_LIMIT_USER_ABUSE':
					const rateLimitErrorMessage = getRateLimitErrorMessage(formatMessage);
					setErrorMessage(rateLimitErrorMessage);
					break;
				default:
					const errorMessageDescriptor = getErrorMessage(node.parameters?.content ?? 'API_FAILED');
					const errorMessage = formatMessage(errorMessageDescriptor);
					setErrorMessage(errorMessage);
					break;
			}
		}
		setTheme(colorMode);
	}, [node.parameters?.macroMetadata?.status, node.parameters?.content, formatMessage, colorMode]);

	useAIPanelAnalytics({ node });

	const doc = React.useMemo(() => {
		if (!node?.content || errorMessage) {
			return null;
		}

		const adfEncoder = new ADFEncoder(() => jsonTransformer);
		return adfEncoder.encode(
			jsonTransformer.encode(
				defaultSchema.nodes.doc.create(
					{},
					Fragment.from(
						createNodesFromContent(node.content as unknown as NodeContent[], defaultSchema),
					),
				),
			),
		);
	}, [node.content, errorMessage]);

	if (errorMessage) {
		return (
			<div css={[prismBackground, theme === 'light' ? prismBackgroundLight : prismBackgroundDark]}>
				<div css={aiPanel}>
					<div css={aiPanelContainer}>
						<SectionMessage appearance="error" title={''}>
							<Text as="p">{errorMessage}</Text>
						</SectionMessage>
					</div>
				</div>
			</div>
		);
	}

	if (!doc) {
		return null;
	}

	return (
		<div css={[prismBackground, theme === 'light' ? prismBackgroundLight : prismBackgroundDark]}>
			<div css={aiPanel}>
				<div css={aiPanelContainer}>
					<RendererActionsContext>
						<ReactRenderer document={doc} appearance="full-width" dataProviders={providerFactory} />
					</RendererActionsContext>
				</div>
			</div>
		</div>
	);
};
